import * as THREE from 'three';
import { Text } from 'troika-three-text';

/* ── stage: paper air, seed grains, one red thread, chapter words ──
   The thread is the founder's path. Each chapter hangs its word deep
   in the air; sparks run forward along the thread; the mechanism
   chapter carries three slow orbits (Partners → YEDI → CFIR). */

const stage = document.getElementById('stage');
const rider = document.getElementById('rider');
const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
let webgl = true;
try { const c = document.createElement('canvas');
      webgl = !!(c.getContext('webgl2') || c.getContext('webgl')); } catch { webgl = false; }

if ((reduced || !webgl) && document.getElementById('rider'))
  document.getElementById('rider').hidden = true;

if (!reduced && webgl) {
  /* is this a phone? drives layout AND render cost (antialias, DPR, texture) */
  const isPortrait = matchMedia('(max-width: 760px)').matches
    || (matchMedia('(pointer: coarse)').matches && innerHeight >= innerWidth);
  const renderer = new THREE.WebGLRenderer({ antialias: !isPortrait, alpha: true });
  /* cap DPR lower on phones — a 3x panel at 2x is a big fill-rate cost for a
     full-screen WebGL scene; 1.75 is still crisp and much lighter on battery.
     AA is off on phones (the higher DPR already smooths edges). */
  renderer.setPixelRatio(Math.min(devicePixelRatio, isPortrait ? 1.75 : 2));
  renderer.setSize(innerWidth, innerHeight);
  stage.appendChild(renderer.domElement);
  /* GPU context loss is common on mobile (backgrounding, memory pressure);
     without this the canvas goes black forever. Keep the context recoverable
     and reveal the readable DOM copy as a fallback. */
  renderer.domElement.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    document.body.classList.add('gl-lost');
  }, false);
  renderer.domElement.addEventListener('webglcontextrestored', () => {
    document.body.classList.remove('gl-lost');
  }, false);

  const scene = new THREE.Scene();
  const texLoader = new THREE.TextureLoader();
  scene.fog = new THREE.Fog(0xF4F4F2, 7, 34);
  const camera = new THREE.PerspectiveCamera(52, innerWidth / innerHeight, .1, 80);
  camera.position.set(0, 0, 10);

  /* PORTRAIT FRAMING — the scene is composed for landscape; on a narrow
     (portrait / phone) screen the horizontal spread overflows. We pull the
     camera back and widen FOV so the whole composition fits. Desktop keeps
     portraitK=1 and is untouched. Recomputed on every resize. */
  let portraitK = 1, pBias = 1;
  function frameForAspect() {
    const a = innerWidth / innerHeight;
    camera.aspect = a;
    if (a < 1) { portraitK = Math.min(1.6, 0.92 / Math.max(a, 0.42)); camera.fov = 58; pBias = 0.4; }
    else { portraitK = 1; camera.fov = 52; pBias = 1; }
    camera.updateProjectionMatrix();
    /* pulling the camera back on portrait pushes content past the fog/fade —
       widen fog to match so nothing dims out */
    if (scene.fog) {
      /* tighter fog on portrait so adjacent chapters' cards/piers fade out
         instead of bleeding into the current screen */
      scene.fog.near = (a < 1 ? 5 : 7) * portraitK;
      scene.fog.far = (a < 1 ? 22 : 34) * portraitK;
    }
  }
  frameForAspect();

  /* seed grains */
  const N = isPortrait ? 650 : 1300;   /* fewer background grains on a phone — battery + fill-rate */
  const pos = new Float32Array(N * 3), col = new Float32Array(N * 3);
  const cInk = new THREE.Color(0x9BA4B2), cRed = new THREE.Color(0xB8222E),
        cWarm = new THREE.Color(0xC9C6BE);
  for (let i = 0; i < N; i++) {
    pos[i*3]   = (Math.random() - .5) * 46;
    pos[i*3+1] = (Math.random() - .5) * 26;
    pos[i*3+2] = -Math.random() * 128;
    const r = Math.random();
    const c = r < .07 ? cRed : (r < .5 ? cInk : cWarm);
    col[i*3] = c.r; col[i*3+1] = c.g; col[i*3+2] = c.b;
  }
  const pgeo = new THREE.BufferGeometry();
  pgeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  pgeo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  const seeds = new THREE.Points(pgeo, new THREE.PointsMaterial({
    size: .05, vertexColors: true, transparent: true, opacity: .85, depthWrite: false,
  }));
  scene.add(seeds);

  /* the red thread */
  const CH = 7, STEP = 16;  /* CH = number of chapters; STEP = spacing between chapter anchors along the path */

  /* ============ CONTENT — all editable text & data in one place ============
     WORDS = 3D chapter words · ORBIT_NAMES = mechanism rings
     RECIPIENTS + IMGMAP = seed grants · PAPERS + PAPER_IMGS = research
     EDU_CARDS = programmes · PARTNERS = bridge piers (last "You?" = open slot) */
  const WORDS = ['CFIR', 'EDUCATION', 'SEEDS', 'RESEARCH', 'MECHANISM', 'PARTNERS', 'ENTER'];
  const ORBIT_NAMES = ['Entrepreneurs', 'YEDI', 'CFIR', 'Partners'];
  const RECIPIENTS = [
    ['Maman Biomedical', 'Pharmaceutical', 'Transforming hormone and specialty drug delivery to be less painful, less wasteful, and more accessible. The first focus is fertility care, where IVF can mean 20+ injections in two weeks — addressed by the Maman Port, Maman Patch, and Maman Sense biosensor.', 'https://mamanbiomedical.ca/'],
    ['Vibraint', 'Medical device', 'Brain-controlled rehabilitation robotics: a non-invasive brain-computer interface combined with AI and VR, designed to restore movement in patients with severe paralysis. Pilot study complete, clinical trials underway.', 'https://vibraint.ai'],
    ['Pill0', 'Biotech', 'Personalized health guidance through a structured questionnaire — instant direction on which medication is right for you, removing the guesswork from the choice.', 'https://www.pill0.com'],
    ['Everyday Chemist', 'Cosmetic R&D', 'A skincare company built on science, family legacy, and intentional craftsmanship. EC Studios, a boutique R&D lab, powers made-to-order, clinically backed formulations.', 'https://www.everyday-chemist.com'],
    ['A+ Wealth', 'Financial services', 'Financial advisory that minimizes high fees while keeping the human touch — making finance accessible across investments, portfolio, wealth, estate, retirement, and insurance.', 'http://www.apluswealth.com'],
    ['Legion IT Services', 'Software development', 'Testing and development services that raise product quality for small and medium IT companies, plus webinars and online courses for QA professionals.', 'https://legion-it.services/'],
    ['Oh-Hi!', 'Networking platform', 'Connecting you with the diamonds around you — a barrier-free platform for professional networking that curates introductions, events, and deal flow matched to your goals.', 'https://www.ohhi-global.com'],
    ['CeoTech', 'IT services', 'An affordable Enhanced Climate Control System that keeps different rooms at their own temperatures independently — zone-by-zone climate management.', 'https://www.linkedin.com/company/ceotech/'],
    ['2XSR', 'Fitness equipment', 'Fitness equipment for athletes, military personnel and outdoor enthusiasts — advanced sandbags, multi-functional rucksacks, weighted vests, and adjustable weight systems.', 'https://www.2xsr.com'],
    ['The Steam Project', 'Educational', 'STEAM-based programming for youth, founded by three science teachers who saw the need for different ways of learning.', 'https://www.thesteamproject.ca/'],
  ];
  const IMGMAP = {
    'Maman Biomedical': '/startup-cards/maman.webp',
    'Vibraint': '/startup-cards/vibraint.webp',
    'Pill0': '/startup-cards/pill0.webp',
    'Everyday Chemist': '/startup-cards/everyday-chemist.webp',
    'A+ Wealth': '/startup-cards/apluswealth.webp',
    'Legion IT Services': '/startup-cards/legion.webp',
    'Oh-Hi!': '/startup-cards/ohhi.webp',
    'CeoTech': '/startup-cards/ceotech.webp',
    '2XSR': '/startup-cards/2xsr.webp',
    'The Steam Project': '/startup-cards/steam.webp',
  };
  const PAPER_IMGS = ['/inside/paper-ai-bars.svg', '/inside/paper-vc-map.svg', '/inside/paper-edu-ridge.svg'];
  const PAPERS = [
    ['The impact of artificial intelligence implementation on the commercial success of startups',
     'Virtual Economics \u00b7 2026',
     'A survey of 68 Canadian startups building on AI, alongside national research-spending data. Funded by CFIR, published open-access.',
     '/research/ai-startups/'],
    ['The role of venture capital funds in dissemination and development of innovation in Canada',
     'Journal of Innovation & Entrepreneurship \u00b7 August 2025',
     'How the concentration of venture capital across the provinces co-moves with firm survival and patent activity. Funded by CFIR, published open-access.',
     '/research/venture-capital/'],
    ['Crafting educational programs in modern business schools: a transformative shift in values and culture',
     'Journal of Interdisciplinary Studies in Education \u00b7 May 2026',
     'How the values and culture taught in modern business schools are changing — and what that shift means for the entrepreneurs they graduate. Funded by CFIR, published open-access.',
     '/research/business-education/'],
  ];
  const EDU_CARDS = [
    ['FOR OWNERS', 'Entrepreneurship & Small Business Management',
     'Building and running a business.',
     'For entrepreneurs and small-business owners — the modern skills to build a company and run it well once it exists: planning, finance, operations, and growth.'],
    ['FOR FOUNDERS', 'Incubator Certificate Program',
     'From a raw idea to a company.',
     'For founders taking a raw idea to a company — a selective programme that shapes the idea into a business plan and an investor pitch, ending at the Venture Fair, where finishers can apply for a seed grant.'],
    ['FOR NON-PROFIT LEADERS', 'Not-for-Profit Certificate Program',
     'Running a mission that lasts.',
     'For the directors and founders of not-for-profits — strengthen how the organisation runs, funds itself, and grows its mission, then pitch it to funders.'],
  ];
  const PARTNERS = ['PARDIC', 'CTI Global', 'Balaj Technologies',
    'Canada Innovation Incubator', 'Canadian Business Growth Advisors',
    'Knoxpro Energy Wiz', 'Disruptors League', 'ArtisTech', 'You?'];
  const FONT_TRY = undefined; /* troika font: undefined = system default */
  /* ============ end CONTENT ============ */


  // main chapter points + a low "dip" after each content chapter: the
  // thread leads between chapters but ducks under the hanging content
  const mains = [];
  for (let i = 0; i <= CH; i++) {
    mains.push(i === CH
      ? new THREE.Vector3(0, 1.75, -i * STEP - 22)
      : new THREE.Vector3(
          (i === 0 ? 1.2 : 2.8) + Math.sin(i * 1.7) * 1.4,
          -1.15 + Math.cos(i * 2.3) * .12,
          /* the finale sits a full chapter deeper: the stretch between
             partners and the end belongs to the bridge alone */
          -i * STEP - (i === 6 ? 20 : 0)
        ));
  }
  const pts = [], chapterNode = [];
  for (let i = 0; i <= CH; i++) {
    chapterNode.push(pts.length);
    pts.push(mains[i]);
    if (i === 5) {
      /* the partners bridge: the deck swings hard right and climbs,
         crosses a flat span, then comes back down to the road —
         the same thread, so the emblem rides straight over it */
      /* Confederation profile: long and low, one gentle navigation
         hump mid-span — no steep arch, clean top line */
      pts.push(new THREE.Vector3(5.4, -.35, -5 * STEP - 14));
      pts.push(new THREE.Vector3(6.2, .32, -5 * STEP - 19.5));
      pts.push(new THREE.Vector3(6.2, .32, -5 * STEP - 25.5));
      pts.push(new THREE.Vector3(4.6, -.5, -5 * STEP - 31));
    } else if ([1, 2, 3].includes(i)) {
      pts.push(new THREE.Vector3(
        mains[i].x * .85, -1.7, -(i + .55) * STEP));
    }
  }
  const nodeT = (idx) => idx / (pts.length - 1);
  const chapT = (i) => nodeT(chapterNode[Math.max(0, Math.min(CH, i))]);
  /* scroll progress (0..1 over 7 chapters) → curve parameter, so the
     camera faces chapter i exactly when its copy is centred on screen */
  const underContent = (i) => curve.getPoint(
    chapT(i) + (chapT(i + 1) - chapT(i)) * .5);
  const beforeChapter = (i) => curve.getPoint(
    Math.max(0, chapT(i) - (chapT(i) - chapT(i - 1)) * .3));
  /* scroll screens → curve chapters. Chapters 0-4 keep the classic
     7/6 lead; the Partners stop (rail dot 5, s=5) is pinned just
     BEFORE the deck climbs — copy live, the emblem still on flat
     road — and the whole bridge is ridden between that stop and
     the finale. */
  /* paced by real distance: ~17 units per screen on the approach,
     across the bridge and out — no surge before the ramp */
  /* the crawl: one whole screen of scroll moves the camera only
     ~7 units around You? — you have to work through it — and the
     road accelerates again on the way out */
  const KNOTS = [0, 7 / 6, 14 / 6, 21 / 6, 28 / 6, 5.33, 5.78, 6.0, 7];
  function pathParam(pp) {
    const s = Math.min(8 - 1e-4, Math.max(0, pp * 8));
    const k = Math.floor(s);
    const f = Math.min(CH - 1e-4,
      KNOTS[k] + (KNOTS[k + 1] - KNOTS[k]) * (s - k));
    const i = Math.floor(f);
    return chapT(i) + (chapT(i + 1) - chapT(i)) * (f - i);
  }
  const curve = new THREE.CatmullRomCurve3(pts);
  const threadGeo = new THREE.TubeGeometry(curve, 360, .032, 12, false);
  const thread = new THREE.Mesh(
    threadGeo,
    new THREE.MeshBasicMaterial({ color: 0xB8222E, transparent: true })
  );
  scene.add(thread);
  /* cap both ends — the tube is hollow and showed a raw cut at start */
  [0, 1].forEach(tEnd => {
    const cap = new THREE.Mesh(
      new THREE.SphereGeometry(.032, 12, 12), thread.material);
    cap.position.copy(curve.getPoint(tEnd));
    scene.add(cap);
  });

  /* sparks running forward along the thread */
  const SPARKS = 22;
  const sparkGeo = new THREE.SphereGeometry(.055, 10, 10);
  const sparks = [];
  for (let i = 0; i < SPARKS; i++) {
    const m = new THREE.Mesh(sparkGeo, new THREE.MeshBasicMaterial({
      color: 0xB8222E, transparent: true, opacity: 0 }));
    scene.add(m); sparks.push(m);
  }

  /* the founder marker */
  const dot = new THREE.Mesh(
    new THREE.SphereGeometry(.15, 24, 24),
    new THREE.MeshBasicMaterial({ color: 0xB8222E })
  );
  dot.visible = false;                 /* the HTML rider is the marker */
  scene.add(dot);
  const halo = new THREE.Mesh(
    new THREE.SphereGeometry(.3, 24, 24),
    new THREE.MeshBasicMaterial({ color: 0xB8222E, transparent: true, opacity: .16 })
  );
  halo.visible = false;
  scene.add(halo);

  /* chapter stations: small red beads on the thread itself */
  const beadMat = new THREE.MeshBasicMaterial({ color: 0xB8222E, transparent: true, opacity: .55 });
  for (let i = 1; i < CH; i++) {
    const tN = nodeT(chapterNode[i]);
    const p0 = curve.getPoint(tN);
    const bead = new THREE.Mesh(new THREE.TorusGeometry(.3, .012, 8, 48), beadMat);
    bead.position.copy(p0);
    bead.lookAt(curve.getPoint(Math.min(1, tN + .005)));
    scene.add(bead);
  }

  /* rounded card shape — the site's card corners, in 3D */
  function roundedGeo(w, hgt, r) {
    const s = new THREE.Shape();
    const x = -w / 2, y = -hgt / 2;
    s.moveTo(x + r, y);
    s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
    s.lineTo(x + w, y + hgt - r); s.quadraticCurveTo(x + w, y + hgt, x + w - r, y + hgt);
    s.lineTo(x + r, y + hgt); s.quadraticCurveTo(x, y + hgt, x, y + hgt - r);
    s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
    return new THREE.ShapeGeometry(s, 12);
  }

  /* tether: a short dashed tie from an object to the thread — one
     visual language for everything that hangs on the founder's path */
  function tether(fromV, toV, color = 0x9BA4B2, opacity = .4) {
    const mid = fromV.clone().lerp(toV, .5); mid.y += .25;
    const bez = new THREE.QuadraticBezierCurve3(fromV, mid, toV);
    const g = new THREE.BufferGeometry().setFromPoints(bez.getPoints(32));
    const m = new THREE.LineDashedMaterial({
      color, transparent: true, opacity, dashSize: .14, gapSize: .1 });
    const line = new THREE.Line(g, m);
    line.computeLineDistances();
    line.renderOrder = -1;                 /* always behind the blocks */
    m.depthWrite = false;
    scene.add(line);
    return line;
  }
  function nearestOnCurve(v) {
    let best = 0, bd = 1e9;
    for (let s = 0; s <= 200; s++) {
      const pt = curve.getPoint(s / 200);
      const d = pt.distanceTo(v);
      if (d < bd) { bd = d; best = s / 200; }
    }
    return curve.getPoint(best);
  }

  /* chapter words hanging deep in the air (troika SDF text) */
  const chWords = [];
  WORDS.forEach((w, i) => {
    const t = new Text();
    t.text = w;
    t.font = FONT_TRY;
    t.fontSize = i === 0 || i === WORDS.length - 1 ? 3.2 : 2.6;
    t.fontWeight = 700;
    t.letterSpacing = .06;
    t.color = 0x14161A;
    t.fillOpacity = .07;
    t.anchorX = 'center'; t.anchorY = 'middle';
    const base = mains[Math.min(CH, i)];
    t.position.set(6.6 + (i % 2) * 1.9 + base.x * .2, base.y * .4 - 2.3, -i * STEP - 9.5);
    t.rotation.y = -.22;
    t.sync();
    scene.add(t); chWords.push(t);
  });

  /* mechanism chapter: three slow orbits around the path */
  const orbitCentre = new THREE.Vector3(mains[4].x + (isPortrait ? 0.7 : 2.7), mains[4].y + (isPortrait ? 2.0 : 2.3), mains[4].z - 14);
  const orbitGroup = new THREE.Group();
  orbitGroup.position.copy(orbitCentre);
  /* inside → out: Entrepreneurs, YEDI, CFIR (red), Partners on top */
  const orbitDefs = [
    { r: .65, c: 0x14161A, tilt: .5 },   /* founders — the core */
    { r: 1.0,  c: 0x9BA4B2, tilt: 1.1 }, /* YEDI */
    { r: 1.35, c: 0xB8222E, tilt: 1.9 }, /* CFIR — red */
    { r: 1.7,  c: 0xC9C6BE, tilt: 2.6 }, /* partners — the outer shell */
  ];
  const orbits = orbitDefs.map((d, oi) => {
    const g = new THREE.Group();
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(d.r, .008, 8, 90),
      new THREE.MeshBasicMaterial({ color: d.c, transparent: true, opacity: .5 }));
    const sat = new THREE.Mesh(
      new THREE.SphereGeometry(.06, 12, 12),
      new THREE.MeshBasicMaterial({ color: d.c }));
    sat.position.x = d.r;
    g.add(ring); g.add(sat);
    g.rotation.x = d.tilt;
    orbitGroup.add(g);
    return { g, sat, r: d.r, speed: .35 / d.r, ang: oi * 1.9 };
  });
  let orbAuto = 1, lastFrameT = 0;   /* self-motion fades out in the hand */
  scene.add(orbitGroup);

  /* grab-and-spin state */
  let orbHover = false, orbDrag = false, orbMoved = false;
  let spinY = 0, spinX = 0, lastPX = 0, lastPY = 0, burstCooldown = 0;
  const orbitHit = new THREE.Mesh(
    new THREE.SphereGeometry(2.4, 12, 12),
    new THREE.MeshBasicMaterial({ visible: false }));
  orbitGroup.add(orbitHit);

  /* the easter egg: overspin and the satellites tear away like an atom
     shedding electrons */
  const BURST_N = 32;
  const burstGeo = new THREE.BufferGeometry();
  burstGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(BURST_N * 3), 3));
  const burstVel = []; let burstLife = 0;
  for (let i = 0; i < BURST_N; i++) burstVel.push(new THREE.Vector3());
  const burst = new THREE.Points(burstGeo, new THREE.PointsMaterial({
    color: 0xB8222E, size: .16, transparent: true, opacity: 0,
    depthWrite: false, fog: false }));
  burst.renderOrder = 9;
  scene.add(burst);
  const _bwp = new THREE.Vector3();
  function shedElectrons() {
    if (burstCooldown > 0) return;
    burstCooldown = 50; burstLife = 1;
    const bp = burst.geometry.attributes.position.array;
    for (let i = 0; i < BURST_N; i++) {
      const src = orbits[i % orbits.length].sat;
      src.getWorldPosition(_bwp);
      bp[i*3] = _bwp.x; bp[i*3+1] = _bwp.y; bp[i*3+2] = _bwp.z;
      burstVel[i].set(
        (Math.random() - .5), (Math.random() - .4), (Math.random() - .5)
      ).normalize().multiplyScalar(.09 + Math.random() * .13);
    }
    burst.geometry.attributes.position.needsUpdate = true;
    spinY *= .3; spinX *= .3;
  }

  /* the Founders core can be spun into a globe — Canada in red:
     these founders are Canada */
  let globeMode = 'off', globeT = 0, globeAge = 0, eggCool = 0, globeReady = false;
  /* unit vector of Canada's heart (lat 50, lon -95) on the unrotated
     sphere — the globe is turned so this point aims at the camera */
  const CANADA_DIR = new THREE.Vector3(-.056, .766, .640).normalize();
  const _gq = new THREE.Quaternion(), _gspin = new THREE.Quaternion(),
        _camW = new THREE.Vector3(), _ctrW = new THREE.Vector3(),
        _yAxis = new THREE.Vector3(0, 1, 0);
  const globe = new THREE.Mesh(
    new THREE.SphereGeometry(.62, 64, 48),
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0 }));
  globe.rotation.y = 2.45;                    /* Canada faces the viewer */
  orbitGroup.add(globe);
  fetch('/inside/world.geo.json').then(r => r.json()).then(w => {
    const cw = isPortrait ? 2048 : 4096, chh = isPortrait ? 1024 : 2048;  /* half-res globe texture on phones */
    const cv = document.createElement('canvas'); cv.width = cw; cv.height = chh;
    const cx = cv.getContext('2d');
    cx.fillStyle = '#F0EDE6'; cx.fillRect(0, 0, cw, chh);
    const X = lon => (lon + 180) / 360 * cw;
    const Y = lat => (90 - lat) / 180 * chh;
    const drawPoly = (rings) => {
      cx.beginPath();
      rings.forEach(ring => {
        ring.forEach(([lon, lat], k) => {
          k ? cx.lineTo(X(lon), Y(lat)) : cx.moveTo(X(lon), Y(lat));
        });
        cx.closePath();
      });
      cx.fill(); cx.stroke();
    };
    cx.lineWidth = 2.4; cx.strokeStyle = '#F4F4F2';
    w.features.forEach(f => {
      cx.fillStyle = f.id === 'CAN' ? '#B8222E' : '#D6D2C8';
      const g = f.geometry;
      if (g.type === 'Polygon') drawPoly(g.coordinates);
      else if (g.type === 'MultiPolygon') g.coordinates.forEach(drawPoly);
    });
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
    globe.material.map = tex;
    globe.material.needsUpdate = true;
    globeReady = true;
  }).catch(() => {});
  function spinEgg() {
    if (eggCool > 0 || !globeReady) return;
    if (globeMode === 'off') { globeMode = 'in'; globeAge = 0; eggCool = 2.5; shedElectrons(); }
    else if (globeMode === 'on') { globeMode = 'out'; eggCool = 2.5; shedElectrons(); }
  }

  const orbitLabels = orbits.map((o, i) => {
    const t = new Text();
    t.text = ORBIT_NAMES[i];
    t.fontSize = isPortrait ? .21 : .16; t.fontWeight = 600; t.letterSpacing = .04;
    t.color = i === 2 ? 0xB8222E : 0x5C5F66; t.fillOpacity = .85;
    t.anchorX = 'left'; t.anchorY = 'middle';
    t.renderOrder = 8;
    t.sync(() => { if (t.material) t.material.depthTest = false; });
    scene.add(t);
    return { t, o };
  });
  const _wp = new THREE.Vector3();

  /* Education: the three streams — three fine lines flowing into the
     thread right where the Education chapter lives */
  const eduJoin = new THREE.Vector3(mains[1].x, .4, mains[1].z - 3.5);
  /* Seeds: the real recipients, scattered as small labels around the path */
  const seedsBase = new THREE.Vector3(mains[2].x, .4, mains[2].z - 6.5);
  const nameTags = [];
  RECIPIENTS.forEach(([name, sec, desc, site], i) => {
    const t = new Text();
    t.text = name;
    t.fontSize = isPortrait ? .34 : .26; t.fontWeight = 600; t.letterSpacing = .02;
    t.color = 0x14161A; t.fillOpacity = .5;
    t.userData.baseColor = 0x14161A;
    t.anchorX = 'center'; t.anchorY = 'middle';
    if (isPortrait) {
      /* portrait: a single column stacked down the path — the two-column
         landscape spread runs off a narrow screen */
      t.position.set(
        seedsBase.x + 1.25,
        seedsBase.y + 2.9 - i * .62,
        seedsBase.z - 2 - i * .5
      );
    } else {
      /* two clean columns to the right of the path — nothing overlaps */
      const colX = [1.0, 3.6][i % 2];
      const row = Math.floor(i / 2);
      t.position.set(
        seedsBase.x + colX,
        seedsBase.y + 2.1 - row * 1.0,
        seedsBase.z - 2 - row * .9 - (i % 2) * .5
      );
    }
    t.sync();
    t.userData.card = {
      k: 'Seed grant recipient', title: name, sub: sec, desc,
      img: IMGMAP[name],
    };
    scene.add(t); nameTags.push({ t, base: t.position.clone() });
  });

  /* Research: the two real papers, spines out along the path */
  const resBase = new THREE.Vector3(mains[3].x, .4, mains[3].z - 8.5);
  const pageEdge = new THREE.LineBasicMaterial({ color: 0x14161A, transparent: true, opacity: .3 });
  const pageFill = new THREE.MeshBasicMaterial({ color: 0xFFFFFF, transparent: true, opacity: .96, side: THREE.DoubleSide });
  const pages = [], paperTags = [];
  PAPERS.forEach(([title, journal, desc, href], i) => {
    const geo = roundedGeo(1.9, 2.5, .16);
    const page = new THREE.Mesh(geo, pageFill.clone());
    const fig = new THREE.Mesh(
      new THREE.PlaneGeometry(1.62, 1.14),
      new THREE.MeshBasicMaterial({ map: texLoader.load(PAPER_IMGS[i]), transparent: true }));
    fig.position.set(0, .52, .01);
    page.add(fig);
    page.position.set(resBase.x + .6 + i * 3.6, resBase.y + .9, resBase.z - 1.5 - i * .4);
    page.rotation.y = -.06;
    page.renderOrder = 3;
    scene.add(page); pages.push(page);
    tether(page.position.clone().add(new THREE.Vector3(0, -1.3, 0)),
           underContent(3));

    /* full paper title below the figure — no journal on the card */
    const tt = new Text();
    tt.text = title;
    tt.fontSize = .135; tt.fontWeight = 700; tt.color = 0x14161A; tt.fillOpacity = .92;
    tt.anchorX = 'center'; tt.anchorY = 'top';
    tt.maxWidth = 1.64; tt.textAlign = 'left'; tt.lineHeight = 1.2;
    tt.position.set(0, -.18, .01);
    page.add(tt); tt.sync();

    page.userData.card = {
      k: 'Applied research \u00b7 open access', title, sub: journal, desc,
      img: PAPER_IMGS[i], imgContain: true,
    };
    paperTags.push(page);
  });

  const dispatchTags = [];

  /* pointer: hover highlights a label, click walks through it */
  const ray = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  let hot = null;
  function pick(cx, cy) {
    ndc.set((cx / innerWidth) * 2 - 1, -(cy / innerHeight) * 2 + 1);
    ray.setFromCamera(ndc, camera);
    const hits = ray.intersectObjects(pickables(), false);
    for (const hh of hits) {
      const o = hh.object;
      /* troika texts can report degenerate full-screen hits at
         distance ~0 (camera inside their bounds) — never real */
      if (hh.distance < 1) continue;
      const op = o.fillOpacity !== undefined
        ? o.fillOpacity : (o.material ? o.material.opacity : 1);
      if (op < .06) continue;      /* dissolved ghosts don't catch clicks */
      return o;
    }
    return null;
  }
  function pickables() {
    return [...nameTags.map(n => n.t), ...dispatchTags, ...paperTags, ...picFrames, ...partnerTags.filter(t => t.userData.card)];
  }
  const orbRay = new THREE.Raycaster();
  function overOrbit(cx, cy) {
    /* the invisible grab-sphere must not swallow clicks from other
       chapters: rays through the research cards used to hit it far
       behind them. It only counts when the mechanism is nearby. */
    if (camera.position.distanceTo(orbitGroup.position) > 20) return false;
    ndc.set((cx / innerWidth) * 2 - 1, -(cy / innerHeight) * 2 + 1);
    orbRay.setFromCamera(ndc, camera);
    return orbRay.intersectObject(orbitHit, false).length > 0;
  }
  let lastMoveT = 0;
  addEventListener('pointerdown', (e) => {
    /* the globe-spin easter egg is desktop-only: on touch it would swallow a
       vertical scroll swipe that happens to start on the globe */
    if (!isPortrait && overOrbit(e.clientX, e.clientY)) {
      orbDrag = true; orbMoved = false;
      spinY = 0; spinX = 0;              /* the hand stops everything */
      lastPX = e.clientX; lastPY = e.clientY;
      lastMoveT = performance.now();
      document.body.style.cursor = 'grabbing';
      e.preventDefault();
    }
  });
  addEventListener('pointerup', () => {
    if (orbDrag) {
      orbDrag = false; document.body.style.cursor = '';
      if (orbMoved && Math.abs(spinY) + Math.abs(spinX) > .05) spinEgg();
    }
  });
  addEventListener('pointermove', (e) => {
    if (orbDrag) {
      const dx = e.clientX - lastPX, dy = e.clientY - lastPY;
      lastPX = e.clientX; lastPY = e.clientY;
      if (Math.abs(dx) + Math.abs(dy) > 2) orbMoved = true;
      orbitGroup.rotation.y += dx * .009;
      orbitGroup.rotation.x += dy * .007;
      /* true flick speed in px/ms — mouse events outpace frames, so
         per-event deltas alone can never reach any threshold */
      const nowT = performance.now();
      const dt = Math.max(1, nowT - lastMoveT);
      lastMoveT = nowT;
      const pxms = Math.hypot(dx, dy) / dt;
      spinY = spinY * .6 + dx * .006 * .4;
      spinX = spinX * .6 + dy * .004 * .4;
      if (pxms > 2.2) spinEgg();            /* a truly hard yank flips the egg */
      return;
    }
    orbHover = overOrbit(e.clientX, e.clientY);
    if (orbHover && !hot) { document.body.style.cursor = 'grab'; }
    else if (!hot) document.body.style.cursor = '';
    const o = pick(e.clientX, e.clientY);
    if (o !== hot) {
      if (hot && hot.color !== undefined && !hot.userData.isPaper)
        hot.color = hot.userData.baseColor ?? 0x14161A;
      if (hot && hot.userData.isPaper) { hot.material.opacity = .96; hot.scale.setScalar(1); }
      hot = o;
      if (hot && !hot.userData.isPaper) hot.color = 0xB8222E;
      if (hot && hot.userData.isPaper) { hot.material.opacity = 1; hot.scale.setScalar(1.045); }
      document.body.style.cursor = hot ? 'pointer' : '';
    }
  }, { passive: true });

  /* the in-place card: blur the world, read, close, continue */
  const ov = document.getElementById('rcov');
  const ovParts = {
    k: document.getElementById('rcov-k'), t: document.getElementById('rcov-t'),
    s: document.getElementById('rcov-s'), d: document.getElementById('rcov-d'),
    a: document.getElementById('rcov-a'),
  };
  const ovImg = document.getElementById('rcov-img');
  let lastFocus = null;
  function showCard(c) {
    if (c.img) { ovImg.decoding = 'async'; ovImg.src = c.img; ovImg.alt = (c.title || '') + ' — figure'; ovImg.hidden = false; }
    else { ovImg.hidden = true; ovImg.removeAttribute('src'); ovImg.alt = ''; }
    ovParts.k.textContent = c.k; ovParts.t.textContent = c.title;
    ovParts.s.textContent = c.sub; ovParts.d.textContent = c.desc;
    if (c.cta && c.href) {
      ovParts.a.hidden = false;
      ovParts.a.textContent = c.cta; ovParts.a.href = c.href;
      if (c.samewindow) { ovParts.a.removeAttribute('target'); }
      else { ovParts.a.setAttribute('target', '_blank'); }
    } else {
      ovParts.a.hidden = true;
    }
    ov.hidden = false;
    document.body.classList.add('ov-open');   /* lock the scroll-driven scene behind */
    lastFocus = document.activeElement;
    requestAnimationFrame(() => {
      ov.classList.add('on');
      const x = document.getElementById('rcov-x'); if (x) x.focus();   /* move focus into the dialog */
    });
  }
  function hideCard() {
    ov.classList.remove('on');
    document.body.classList.remove('ov-open');
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }   /* restore focus */
    setTimeout(() => { ov.hidden = true; }, 300);
  }
  /* figure zoom: tap the image to see it full-screen */
  const rz = document.getElementById('rzoom');
  const rzImg = document.getElementById('rzoom-img');
  ovImg.addEventListener('click', () => {
    if (!ovImg.src) return;
    rzImg.src = ovImg.src; rzImg.alt = ovImg.alt || '';
    rz.hidden = false;
    requestAnimationFrame(() => rz.classList.add('on'));
  });
  function hideZoom() {
    rz.classList.remove('on');
    setTimeout(() => { rz.hidden = true; }, 250);
  }
  rz.addEventListener('click', hideZoom);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !rz.hidden) hideZoom();
  });

  document.getElementById('rcov-x').addEventListener('click', hideCard);
  ov.addEventListener('click', (e) => { if (e.target === ov) hideCard(); });
  /* swipe the bottom sheet down to dismiss (mobile) */
  (() => {
    const card = ov.querySelector('.rcov-card');
    let sy = 0, dy = 0, dragging = false;
    card.addEventListener('touchstart', (e) => {
      if (card.scrollTop > 0) return;          /* let inner scroll win first */
      sy = e.touches[0].clientY; dy = 0; dragging = true; card.style.transition = 'none';
    }, { passive: true });
    card.addEventListener('touchmove', (e) => {
      if (!dragging) return;
      dy = e.touches[0].clientY - sy;
      if (dy > 0) card.style.transform = 'translateY(' + dy + 'px)';
    }, { passive: true });
    card.addEventListener('touchend', () => {
      if (!dragging) return; dragging = false; card.style.transition = ''; card.style.transform = '';
      if (dy > 90) hideCard();
    });
  })();
  document.addEventListener('keydown', (e) => {
    if (ov.hidden) return;
    if (e.key === 'Escape') { hideCard(); return; }
    /* keep Tab inside the open dialog (simple focus trap) */
    if (e.key === 'Tab') {
      const f = [...ov.querySelectorAll('button, a[href]')].filter(el => !el.hidden && el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  addEventListener('click', (e) => {
    if (!ov.hidden) return;
    if (e.target && e.target.closest && e.target.closest('a, button')) return;
    if (orbMoved) { orbMoved = false; return; }  /* it was a spin, not a click */
    if (overOrbit(e.clientX, e.clientY)) return;
    const o = pick(e.clientX, e.clientY);
    if (!o) return;
    if (o.userData.card) showCard(o.userData.card);
    else if (o.userData.href) location.href = o.userData.href;
  });
  dispatchTags.forEach(t => { t.userData.isDispatch = true; });
  paperTags.forEach(t => { t.userData.isPaper = true; });

  /* Education: three clean cards — WHO it is for, the programme's
     full name, one line of what it gives, and the maple mark.
     No photos, no logos: the fan of three says "we hand this out,
     and each kind of founder gets their own". */
  const founderPics = [], picFrames = [];
  EDU_CARDS.forEach(([who, programme, gives, full], i) => {
    const group = new THREE.Group();
    const CW = 2.55, CHH = 1.6, M = .2;
    const shadow = new THREE.Mesh(
      roundedGeo(CW + .22, CHH + .22, .16),
      new THREE.MeshBasicMaterial({ color: 0x14161A, transparent: true, opacity: .04 }));
    shadow.position.set(.02, -.03, -.014);
    shadow.userData.op = .04;
    const edge = new THREE.Mesh(
      roundedGeo(CW + .016, CHH + .016, .065),
      new THREE.MeshBasicMaterial({ color: 0x14161A, transparent: true, opacity: .09 }));
    edge.position.z = -.005;
    edge.userData.op = .09;
    const frame = new THREE.Mesh(
      roundedGeo(CW, CHH, .06),
      new THREE.MeshBasicMaterial({ color: 0xFDFCFA, transparent: true, opacity: .98 }));
    frame.userData.op = .98;
    const sealBed = new THREE.Mesh(
      new THREE.CircleGeometry(.17, 28),
      new THREE.MeshBasicMaterial({ color: 0xB8222E, transparent: true, opacity: .05 }));
    sealBed.position.set(CW / 2 - .34, -CHH / 2 + .34, .008);
    sealBed.userData.op = .05;
    const sealRing = new THREE.Mesh(
      new THREE.RingGeometry(.165, .178, 32),
      new THREE.MeshBasicMaterial({ color: 0xB8222E, transparent: true, opacity: .38 }));
    sealRing.position.set(CW / 2 - .34, -CHH / 2 + .34, .009);
    sealRing.userData.op = .38;
    const seal = new THREE.Mesh(
      new THREE.PlaneGeometry(.2, .2),
      new THREE.MeshBasicMaterial({
        map: texLoader.load('/brand/cfir-mark-leaf-lightbulb.png'),
        transparent: true }));
    seal.position.set(CW / 2 - .34, -CHH / 2 + .34, .01);
    seal.userData.op = .9;
    group.add(shadow, edge, frame, sealBed, sealRing, seal);

    const kick = new Text();
    kick.text = who;
    kick.fontSize = .08; kick.fontWeight = 600; kick.letterSpacing = .26;
    kick.color = 0xB8222E; kick.fillOpacity = .8;
    kick.anchorX = 'left'; kick.anchorY = 'top';
    kick.position.set(-CW / 2 + M, CHH / 2 - .2, .01);
    kick.sync();
    group.add(kick);

    const cap = new Text();
    cap.text = programme;
    cap.fontSize = .165; cap.fontWeight = 700; cap.lineHeight = 1.14;
    cap.color = 0x14161A; cap.fillOpacity = .96;
    cap.anchorX = 'left'; cap.anchorY = 'top';
    cap.maxWidth = CW - 2 * M;
    cap.position.set(-CW / 2 + M, CHH / 2 - .42, .01);
    cap.sync();
    group.add(cap);

    const gv = new Text();
    gv.text = gives;
    gv.fontSize = .105; gv.fontWeight = 500;
    gv.color = 0x6B6F76; gv.fillOpacity = .92;
    gv.anchorX = 'left'; gv.anchorY = 'top';
    gv.maxWidth = CW - 2 * M - .5;
    gv.position.set(-CW / 2 + M, -CHH / 2 + .42, .01);
    gv.sync();
    group.add(gv);

    /* dealt places: Incubator takes the free air up on the LEFT,
       the other two keep their spots — nobody overlaps anybody */
    const OFF = [[1.75, 1.15, -2], [-2.45, 2.05, -2.8], [-1.25, -.2, -3.8]][i];
    group.position.set(
      eduJoin.x + OFF[0], eduJoin.y + OFF[1], eduJoin.z + OFF[2]);
    group.renderOrder = 3;
    /* pile order = final depth order: the card that lands closest
       lies on top, so nothing flips over anything while dealing */
    const final = group.position.clone();
    const stack = new THREE.Vector3(
      eduJoin.x + 1.5,
      eduJoin.y + .05 + (2 - i) * .05,
      eduJoin.z - 3.2 - i * .06);
    const tilt = [-.07, .045, -.025][i];
    frame.userData.card = {
      k: 'Education', title: programme,
      sub: who, desc: full,
    };
    frame.userData.isPaper = true;
    scene.add(group);
    founderPics.push({ group, i, final, stack, tilt });
    picFrames.push(frame);
  });

  /* Partners — the simple read that worked: the red deck carried by
     slim red strip piers, one name written up each strip in paper
     white. Feet flare a little at the base, so the strips visibly
     stand on something — the ground itself is never drawn. You? is
     the dashed, unbuilt strip at the faint far end. */
  /* arch order: the longest name gets the mid-span pier, the short
     ones close the ends — the pier length IS the name length */
  const partnerTags = [];
  const piers = [];
  const pierNames = [];
  const pT5 = chapT(5), pT6 = chapT(6);
  const P_BASE = -2.35, P_FOOT = .42;
  const RED = 0xB8222E;
  PARTNERS.forEach((name, s) => {
    const isYou = name === 'You?';
    const tt = pT5 + (.2 + .075 * s) * (pT6 - pT5);
    const P = curve.getPoint(tt);
    const g = new THREE.Group();                 /* stick: roadway-fixed */
    g.position.set(P.x, P.y, P.z);
    const tanP = curve.getTangent(tt);
    const slope = tanP.y / Math.hypot(tanP.x, tanP.z);
    g.rotation.y = Math.atan2(-tanP.z, tanP.x);
    scene.add(g);
    const fg = new THREE.Group();                /* foot + name: face viewer */
    fg.position.set(P.x, P.y, P.z);
    scene.add(fg);
    piers.push({ g, s, fg });

    /* the plaque relay: one red board with a white name floats over
       each pier, but it only lives while the emblem circle rides the
       stretch between THIS pier and the next — a hand-off of names
       along the crossing */
    const pfg = new THREE.Group();
    pfg.position.set(P.x, P.y, P.z);
    scene.add(pfg);
    const t = new Text();
    t.text = name;
    t.fontSize = isYou ? .22 : .17;
    t.fontWeight = 600;
    t.color = isYou ? RED : 0xFFFFFF;
    t.userData.baseColor = isYou ? RED : 0xFFFFFF;
    t.fillOpacity = .98;
    t.anchorX = 'center'; t.anchorY = 'middle';
    t.position.set(0, .62, .12);
    if (isYou) t.userData.card = {
      k: 'Partners', title: 'You?',
      sub: '',
      desc: 'Partners agree with the mission and provide the capital — capital that becomes education, seed grants, and research for Canadian entrepreneurs. There is room for one more.',
      cta: 'Partner with us →', href: '/partner-with-us/', samewindow: true,
    };
    pfg.add(t); partnerTags.push(t);
    t.sync(() => {
      if (!t.textRenderInfo) return;
      const bb = t.textRenderInfo.blockBounds;
      const w = bb[2] - bb[0] + .3, hh = bb[3] - bb[1] + .18;
      if (isYou) {
        const so = new THREE.Shape();
        const x0 = -w / 2, y0 = -hh / 2, r0 = Math.min(.12, hh / 2 - .02);
        so.moveTo(x0 + r0, y0);
        so.lineTo(x0 + w - r0, y0); so.quadraticCurveTo(x0 + w, y0, x0 + w, y0 + r0);
        so.lineTo(x0 + w, y0 + hh - r0); so.quadraticCurveTo(x0 + w, y0 + hh, x0 + w - r0, y0 + hh);
        so.lineTo(x0 + r0, y0 + hh); so.quadraticCurveTo(x0, y0 + hh, x0, y0 + hh - r0);
        so.lineTo(x0, y0 + r0); so.quadraticCurveTo(x0, y0, x0 + r0, y0);
        const ol = new THREE.LineLoop(
          new THREE.BufferGeometry().setFromPoints(
            so.getPoints(10).map(v => new THREE.Vector3(v.x, v.y, 0))),
          new THREE.LineDashedMaterial({ color: RED,
            dashSize: .09, gapSize: .07, transparent: true, opacity: .9 }));
        ol.computeLineDistances();
        ol.userData.op = .9;
        ol.position.set(0, .62, .1);
        pfg.add(ol);
      } else {
        const bg = new THREE.Mesh(
          roundedGeo(w, hh, Math.min(.12, hh / 2 - .02)),
          new THREE.MeshBasicMaterial({ color: RED, transparent: true, opacity: .95 }));
        bg.userData.op = .95;
        bg.position.set(0, .62, .1);
        pfg.add(bg);
      }
    });
    pierNames.push({ t, pfg, tt, isYou });

    /* fixed geometry: sticks all reach one shared heel line — no
       dancing depths; the name stands to the RIGHT of its pier,
       raised for readability */
    const yF2 = P_BASE + P_FOOT - P.y, yB2 = P_BASE - P.y;
    if (isYou) {
      const sp2 = new THREE.Shape();
      sp2.moveTo(-.07, .018 - slope * .07);
      sp2.lineTo(.07, .018 + slope * .07);
      sp2.lineTo(.07, yF2); sp2.lineTo(.17, yB2);
      sp2.lineTo(-.17, yB2); sp2.lineTo(-.07, yF2);
      sp2.closePath();
      const ln = new THREE.LineLoop(
        new THREE.BufferGeometry().setFromPoints(
          sp2.getPoints(4).map(v => new THREE.Vector3(v.x, v.y, 0))),
        new THREE.LineDashedMaterial({ color: RED,
          dashSize: .11, gapSize: .09, transparent: true, opacity: .7 }));
      ln.computeLineDistances();
      g.add(ln);
    } else {
      const stickSh = new THREE.Shape();
      stickSh.moveTo(-.07, .018 - slope * .07);
      stickSh.lineTo(.07, .018 + slope * .07);
      stickSh.lineTo(.07, yF2); stickSh.lineTo(-.07, yF2);
      stickSh.closePath();
      g.add(new THREE.Mesh(
        new THREE.ShapeGeometry(stickSh),
        new THREE.MeshBasicMaterial({ color: RED, side: THREE.DoubleSide })));
      const fsh = new THREE.Shape();
      fsh.moveTo(-.07, yF2 + .28); fsh.lineTo(.07, yF2 + .28);
      fsh.lineTo(.07, yF2); fsh.lineTo(.17, yB2);
      fsh.lineTo(-.17, yB2); fsh.lineTo(-.07, yF2);
      fsh.closePath();
      const footM = new THREE.Mesh(
        new THREE.ShapeGeometry(fsh),
        new THREE.MeshBasicMaterial({ color: RED, side: THREE.DoubleSide }));
      footM.position.z = .09;
      fg.add(footM);
    }
  });

  const endP = mains[CH].clone();

  /* fog breathes with the chapters — paper, warm, cool, blush, paper */
  const TONES = [0xF4F4F2, 0xF3EFE8, 0xEFF2F0, 0xF6EFED, 0xF0F1F3, 0xF3EFE8, 0xF4F4F2, 0xF4F4F2]
    .map(c => new THREE.Color(c));
  const fogTone = new THREE.Color(0xF4F4F2);

  /* scroll + mouse parallax */
  let progress = 0, target = 0, mx = 0, my = 0, tmx = 0, tmy = 0;
  let riderDocked = false;
  const onScroll = () => {
    const max = document.documentElement.scrollHeight - innerHeight;
    target = max > 0 ? scrollY / max : 0;
  };
  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('scroll', () => document.body.classList.add('moved'),
    { once: true, passive: true });
  addEventListener('mousemove', (e) => {
    tmx = (e.clientX / innerWidth - .5);
    tmy = (e.clientY / innerHeight - .5);
  }, { passive: true });
  onScroll();

  const clock = new THREE.Clock();
  let loopErrLogged = false;
  renderer.setAnimationLoop(() => {
   try {
    const t = clock.getElapsedTime();

    /* arrival: the thread fades in over the first moments */
    const arrive = Math.min(1, t / 1.6);
    /* adaptive catch-up: rail jumps fly faster, small moves stay soft */
    const gap = Math.abs(target - progress);
    progress += (target - progress) * (isPortrait
      ? (gap > .3 ? .34 : gap > .12 ? .26 : .16)   /* tighter on touch: the dot tracks the thumb along the thread */
      : (gap > .3 ? .22 : gap > .12 ? .14 : .06));
    mx += (tmx - mx) * .05; my += (tmy - my) * .05;
    const p = Math.min(.985, Math.max(0, progress));
    /* the finale stands alone: everything but the thread hushes */
    const hush = Math.min(1, Math.max(0, (p - .885) / .075));
    /* troika text ignores scene fog — fade it by depth manually so
       chapters stop bleeding into each other */
    const depthVis = (obj) => {
      const d = camera.position.distanceTo(obj.position) / portraitK;
      /* tighter fade band on a phone so only the CURRENT chapter's text shows —
         a wide band let the next/previous chapters bleed into the same screen */
      const near = isPortrait ? 17 : 26, span = isPortrait ? 5.5 : 9;
      return Math.min(1, Math.max(0, (near - d) / span));
    };

    const pp = pathParam(p);
    const cam = curve.getPoint(pp);
    const ahead = curve.getPoint(Math.min(1, pp + .012));
    camera.position.set(cam.x * .1 - .4 * pBias + mx * .9, Math.max(-.35, cam.y * .18) + .2 - my * .6, cam.z + 7.5 * portraitK);
    camera.lookAt(ahead.x * .55 + .4 * pBias + mx * 1.6, Math.max(-.6, ahead.y * .35) - my * 1.1, ahead.z);

    /* the marker leaves the very start of the line and arrives at the
       end IN SYNC with the finale copy — its lead melts away with p */
    /* the emblem sits ON the very tip of the thread at page start and
       is carried by the line itself — no artificial lead */
    const dp = Math.min(1, pp);
    dot.position.copy(curve.getPoint(dp));
    /* project the marker onto the screen: the rider (the real emblem,
       HTML) sits exactly on the line, from the very start to the end */
    const proj = dot.position.clone().project(camera);
    const behind = proj.z > 1;
    if (rider) {
      /* on the line, all the way to its end — no early departure */
      let rx = (proj.x * .5 + .5) * innerWidth;
      let ry = (-proj.y * .5 + .5) * innerHeight;
      let scale = (34 / 86) * Math.min(1.4, Math.max(.5, 9 / camera.position.distanceTo(dot.position)));
      /* one threshold both ways: dock exactly where the line meets the
         slot, release at the same point on the way back — symmetric */
      riderDocked = p >= .983;
      if (riderDocked) {
        const slot = document.getElementById('emblem-slot');
        const r = slot && slot.getBoundingClientRect();
        if (r && r.width) {
          /* the text has arrived underneath — it catches the circle and
             carries it from here */
          rx = r.left + r.width / 2;
          ry = r.top + r.height / 2;
          scale = 1;
        }
      }
      rider.hidden = behind && !riderDocked;
      rider.style.transform =
        `translate(${rx - 43}px, ${ry - 43}px) scale(${scale})`;
    }

    /* sparks: forward drift along the thread, fading near the ends */
    for (let i = 0; i < SPARKS; i++) {
      const sp = (pp + .015 + ((t * .012) + i / SPARKS) % .18) % 1;
      const m = sparks[i];
      m.position.copy(curve.getPoint(Math.min(1, sp)));
      const ahead01 = (sp - pp) / .2;
      m.material.opacity = ahead01 > 0 && ahead01 < 1
        ? .55 * Math.sin(Math.PI * Math.min(1, Math.max(0, ahead01))) : 0;
    }

    /* grab-and-spin: only direct contact moves it; while held, ALL
       self-motion freezes — the hand is the only mover */
    const dtl = Math.min(.06, t - lastFrameT); lastFrameT = t;
    orbAuto += ((orbDrag ? 0 : 1) - orbAuto) * (orbDrag ? .25 : .05);
    const speedBoost = 1 + (Math.abs(spinY) + Math.abs(spinX)) * 26;
    if (eggCool > 0) eggCool -= dtl;
    if (globeMode === 'in' || globeMode === 'on') globeT = Math.min(1, globeT + dtl * .9);
    if (globeMode === 'out') { globeT = Math.max(0, globeT - dtl * 1.4); if (!globeT) globeMode = 'off'; }
    if (globeMode === 'in' && globeT >= 1) globeMode = 'on';
    orbits.forEach((o, oi) => {
      o.ang += dtl * o.speed * speedBoost * orbAuto;
      o.g.rotation.z = o.ang;
      o.g.children[0].material.opacity = orbHover || orbDrag ? .85 : .5;
      /* the inner orbit vanishes outright while the globe exists */
      if (oi === 0) o.g.visible = globeT < .35;
    });
    globe.visible = globeT > 0;
    if (globe.visible) {
      globe.material.opacity = globeT;
      /* the globe does not spin: Canada aims at the camera, north up,
         whatever the figure or camera are doing; only the satellites
         keep orbiting around it */
      camera.getWorldPosition(_camW);
      globe.getWorldPosition(_ctrW);
      const dirW = _camW.sub(_ctrW).normalize();
      orbitGroup.getWorldQuaternion(_gq).invert();
      const dirL = dirW.applyQuaternion(_gq).normalize();
      globe.quaternion.setFromUnitVectors(CANADA_DIR, dirL);
      /* roll correction: keep the north pole pointing up on screen */
      const northL = _yAxis.clone().applyQuaternion(globe.quaternion);
      const upL = _yAxis.clone().applyQuaternion(_gq);
      const a = northL.addScaledVector(dirL, -northL.dot(dirL)).normalize();
      const b = upL.addScaledVector(dirL, -upL.dot(dirL)).normalize();
      const roll = Math.atan2(a.clone().cross(b).dot(dirL), a.dot(b));
      globe.quaternion.premultiply(_gspin.setFromAxisAngle(dirL, roll));
    }
    if (!orbDrag) {
      orbitGroup.rotation.y += .0008 * orbAuto + spinY;
      orbitGroup.rotation.x += spinX;
      spinY *= .985; spinX *= .985;             /* the spin winds down */
    }
    burstCooldown = Math.max(0, burstCooldown - 1);

    /* overspin → the core flips between ring and globe */
    if (Math.abs(spinY) + Math.abs(spinX) > .12) spinEgg();
    if (burstLife > 0) {
      burstLife = Math.max(0, burstLife - .014);
      const bp = burst.geometry.attributes.position.array;
      for (let i = 0; i < BURST_N; i++) {
        bp[i*3] += burstVel[i].x; bp[i*3+1] += burstVel[i].y; bp[i*3+2] += burstVel[i].z;
        burstVel[i].multiplyScalar(.985);
      }
      burst.geometry.attributes.position.needsUpdate = true;
      burst.material.opacity = burstLife * .9;
    } else { burst.material.opacity = 0; }
    const orbVis = depthVis(orbitGroup) * (1 - hush);  /* stable, no jitter */
    orbitLabels.forEach(({ t: lbl, o }, li) => {
      if (li === 0) lbl.visible = globeT < .5;
      o.sat.getWorldPosition(_wp);
      lbl.position.set(_wp.x + .12, _wp.y + .1, _wp.z);
      lbl.quaternion.copy(camera.quaternion);
      lbl.fillOpacity = .85 * orbVis;
      if (lbl.material && lbl.material.depthTest) lbl.material.depthTest = false;
    });

    /* finale synchrony: the thread dissolves as the marker arrives,
       then hands over to the HTML emblem above the copy */
    thread.material.opacity = (1 - hush * .96) * arrive;

    /* breathing tone */
    const fi = Math.min(TONES.length - 2, Math.floor(p * CH));
    fogTone.copy(TONES[fi]).lerp(TONES[fi + 1], p * CH - fi);
    scene.fog.color.copy(fogTone);
    document.body.style.background = '#' + fogTone.getHexString();


    /* chapter words are texture, not walls: dissolve as we fly through */
    chWords.forEach(w => {
      const d = camera.position.distanceTo(w.position);
      w.fillOpacity = .07 * Math.min(1, Math.max(0, (d - 9) / 11)) * (1 - hush);
    });
    dispatchTags.forEach(t => { t.fillOpacity = .55 * (1 - hush); });
    /* the bridge assembles as it comes into view: piers grow down
       to their ice shields one after another, plaques follow, the
       dashed You? arrives last */
    /* the relay: the emblem's own curve position decides which
       plaque is alive — first one early, You? lingers past its pier */
    const rT = pathParam(p);
    const pnSpacing = pierNames.length > 1
      ? pierNames[1].tt - pierNames[0].tt : .01;
    pierNames.forEach((pn, i) => {
      const from = i === 0 ? pn.tt - pnSpacing * 2.5 : pn.tt;
      /* You? never expires by position — once reached it stays all
         the way to the finale (hush hides it there), so even a fast
         flick can't rip it away unread */
      const until = i < pierNames.length - 1
        ? pierNames[i + 1].tt : pn.tt + pnSpacing * 40;
      const fw = pnSpacing * .22;
      const a = Math.min(1, Math.max(0,
        Math.min((rT - from) / fw, (until - rT) / fw))) * (1 - hush);
      pn.pfg.visible = a > .02;
      if (!pn.pfg.visible) return;
      pn.pfg.rotation.y = Math.atan2(
        camera.position.x - pn.pfg.position.x,
        camera.position.z - pn.pfg.position.z);
      const dT = camera.position.distanceTo(pn.pfg.position);
      pn.pfg.scale.setScalar(Math.min(2.2, Math.max(.9, dT / 9)));
      pn.pfg.children.forEach(c => {
        if (c.fillOpacity !== undefined) c.fillOpacity = .98 * a;
        else if (c.material) c.material.opacity = (c.userData.op ?? .95) * a;
      });
    });
    piers.forEach(pr => {
      pr.g.visible = hush < .95;
      if (pr.fg) {
        pr.fg.rotation.y = Math.atan2(
          camera.position.x - pr.fg.position.x,
          camera.position.z - pr.fg.position.z);
        pr.fg.visible = pr.g.visible;
      }
    });
    seeds.material.opacity = .85 - .55 * hush;

    /* everything with a face turns to the viewer */
    nameTags.forEach(n => n.t.quaternion.copy(camera.quaternion));
    paperTags.forEach(pg => pg.quaternion.copy(camera.quaternion));
    /* the diplomas are dealt: one neat pile, fanning out to three
       as the camera arrives — "So we make it free" made visible */
    const sEdu = p * 8;
    founderPics.forEach((f, i2) => {
      /* dealt one after another, in order — no card crosses another
         mid-flight */
      const dt = Math.min(1, Math.max(0, (sEdu - .42 - i2 * .17) / .4));
      const deal = dt * dt * (3 - 2 * dt);
      f.group.quaternion.copy(camera.quaternion);
      f.group.position.lerpVectors(f.stack, f.final, deal);
      f.group.position.y += Math.sin(t * .5 + i2 * 2.1) * .05 * deal;
      f.group.rotateZ(f.tilt * (1 - deal));
      const dN = camera.position.distanceTo(f.group.position);
      const near = Math.min(1, Math.max(0, (dN - 2.5) / 2));
      const v = depthVis(f.group) * near * (1 - hush);
      f.group.visible = v > .02;
      f.group.children.forEach(c => {
        if (c.fillOpacity !== undefined) c.fillOpacity = .92 * v;
        else if (c.material) c.material.opacity = (c.userData.op ?? .96) * v;
      });
    });

    /* recipient names sway gently */
    nameTags.forEach((n, i) => {
      n.t.position.y = n.base.y + Math.sin(t * .6 + i * 1.7) * .12;
      /* dissolve as the camera gets close — never a giant word in the face */
      const d = camera.position.distanceTo(n.t.position);
      n.t.fillOpacity = .5 * Math.min(1, Math.max(0, (d - 1.5) / 2.5))
        * depthVis(n.t) * (1 - hush);
    });
    pages.forEach((pg, i) => {
      pg.rotation.y += Math.sin(t * .4 + i) * .0004;
      const v = depthVis(pg.position ? pg : pg);
      pg.children.forEach(c => {
        if (c.fillOpacity !== undefined)
          c.fillOpacity = (c.color === 0xB8222E ? .9 : .92) * Math.max(v, .0);
      });
    });

    seeds.rotation.z = t * .008;
    renderer.render(scene, camera);
   } catch (err) {
    /* keep the loop alive: log the first failure but never blank the scene */
    if (!loopErrLogged) { console.error('LOOP-FAIL', err && err.message); loopErrLogged = true; }
   }
  });

  let lastW = innerWidth, lastH = innerHeight;
  addEventListener('resize', () => {
    /* on a phone the address bar hides/shows on scroll, firing resize with only
       a height change — a full setSize/reflow every scroll frame is wasteful and
       janky. React to width changes always; to height only when it's a real
       change (orientation / keyboard), not the ~address-bar delta. */
    const dw = Math.abs(innerWidth - lastW), dh = Math.abs(innerHeight - lastH);
    if (dw === 0 && dh < 120 && isPortrait) return;
    lastW = innerWidth; lastH = innerHeight;
    frameForAspect();
    renderer.setSize(innerWidth, innerHeight);
  });
}

/* chapter entrances + journey rail */
const chapters = [...document.querySelectorAll('.ch')];
const railDots = [...document.querySelectorAll('.rail a')];
/* the copy lives at the bottom and SWAPS in place: the chapter under
   the scroll position is the live one, others fade where they stand */
function railSync() {
  /* the old copy holds the floor right up to the swap — an early
     handoff leaves a blank, because the incoming slide physically
     arrives from below only at the .5 boundary */
  const idx = Math.min(chapters.length - 1,
    Math.round(scrollY / innerHeight));
  /* both bridge screens keep the Partners copy and dot alive */
  const live = (idx === 6 || idx === 7) ? 5 : idx;
  const dot = idx >= 8 ? 6 : Math.min(idx, 5);
  railDots.forEach((d, i) => d.classList.toggle('on', i === dot));
  chapters.forEach((c, i) => {
    c.classList.toggle('live', i === live);
    if (i === live) c.querySelector('.ch-in').classList.add('on');
  });
}
addEventListener('scroll', railSync, { passive: true });
railSync();
/* sticky slides confuse anchor jumps upward — drive the scroll directly */
const railEl = document.querySelector('.rail');
if (railEl) addEventListener('mousemove', (e) => {
  railEl.classList.toggle('near', e.clientX < 150);
}, { passive: true });
railDots.forEach((d, i) => {
  d.addEventListener('click', (e) => {
    e.preventDefault();
    window.scrollTo({ top: (i === 6 ? 8 : i) * innerHeight, behavior: 'smooth' });
  });
});
if (reduced) document.querySelectorAll('.ch-in').forEach(el => el.classList.add('on'));

/* Latest from the Newsroom */
/* Latest from the Newsroom — pulled from the shared latest-news.json
   (written by scripts/newsroom-rebuild.py from the article corpus);
   publish an article, rerun the generator, this list updates itself */
fetch('/latest-news.json').then(r => r.json()).then(list => {
  const box = document.getElementById('fresh');
  if (!box) return;
  if (!Array.isArray(list) || !list.length) { box.hidden = true; return; }  /* no orphaned label */
  list.slice(0, 3).forEach(it => {
    const a = document.createElement('a');
    a.href = it.url; a.textContent = it.title;
    box.appendChild(a);
  });
}).catch(() => { const box = document.getElementById('fresh'); if (box) box.hidden = true; });
